import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "synthwave-grid";

const DEFAULT_SKY_TOP = "#0b1227";
const DEFAULT_SKY_BOTTOM = "#7c2d6f";
const DEFAULT_GRID = "#ec4899";
const DEFAULT_SUN = "#fbbf24";

const HORIZON_FRAC = 0.55;       // where the horizon sits (vertical fraction)
const VERTICAL_LINES = 21;       // odd, so the centerline is symmetric
const HORIZONTAL_LINES = 22;     // virtual "rows" before they're recycled
const SCROLL_SPEED = 0.012;      // grid-lines-per-frame moving forward
const VANISH_X_FRAC = 0.5;       // where vertical lines converge on the horizon
const SUN_HEIGHT_FRAC = 0.32;    // sun radius as fraction of canvas height
const SUN_Y_OFFSET_FRAC = 0.04;  // how far the sun dips below horizon (kept hidden)

export default function SynthwaveGrid({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef({
    skyTop: DEFAULT_SKY_TOP,
    skyBottom: DEFAULT_SKY_BOTTOM,
    grid: DEFAULT_GRID,
    sun: DEFAULT_SUN,
  });

  useEffect(() => {
    colorsRef.current = {
      skyTop: pickColor(colors, 0, DEFAULT_SKY_TOP),
      skyBottom: pickColor(colors, 1, DEFAULT_SKY_BOTTOM),
      grid: pickColor(colors, 2, DEFAULT_GRID),
      sun: pickColor(colors, 3, DEFAULT_SUN),
    };
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    let scroll = 0;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const drawSky = (top, bottom) => {
      const grad = ctx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, top);
      grad.addColorStop(1, bottom);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);
    };

    const drawSun = (sun, sky) => {
      const horizonY = height * HORIZON_FRAC;
      const radius = height * SUN_HEIGHT_FRAC;
      // Center is just below the horizon so only the upper hemisphere shows.
      const cx = width / 2;
      const cy = horizonY + height * SUN_Y_OFFSET_FRAC;

      // Vertical sun gradient — bright at top, fading into the sky color.
      const grad = ctx.createLinearGradient(cx, cy - radius, cx, cy + radius);
      grad.addColorStop(0, rgba(sun, 1));
      grad.addColorStop(0.55, rgba(sun, 0.85));
      grad.addColorStop(1, rgba(sky, 0));
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fill();

      // Horizontal slats — the iconic "sliced" sun. Each slat hides a
      // progressively wider chord of the sun toward the horizon.
      ctx.fillStyle = sky;
      const slatCount = 7;
      for (let i = 0; i < slatCount; i += 1) {
        const t = i / slatCount;
        // Slats are bunched toward the bottom of the sun (closer to horizon).
        const slatY = cy - radius * 0.05 + (radius * 0.95) * (t * t);
        const slatHeight = 2 + i * 1.2;
        ctx.fillRect(cx - radius * 1.05, slatY, radius * 2.1, slatHeight);
      }
    };

    const drawGrid = (gridColor) => {
      const horizonY = height * HORIZON_FRAC;
      const vanishX = width * VANISH_X_FRAC;
      const groundHeight = height - horizonY;

      ctx.lineWidth = 1.2;

      // Vertical lines: fan out from the vanishing point on the horizon.
      // Past the canvas edges so it always feels like the grid extends out.
      ctx.strokeStyle = rgba(gridColor, 0.7);
      const span = width * 1.6; // total fanned-out width at the bottom edge
      for (let i = 0; i < VERTICAL_LINES; i += 1) {
        const t = i / (VERTICAL_LINES - 1); // 0..1
        const xBottom = vanishX - span / 2 + t * span;
        ctx.beginPath();
        ctx.moveTo(vanishX, horizonY);
        ctx.lineTo(xBottom, height);
        ctx.stroke();
      }

      // Horizontal lines: spaced via a quadratic so the spacing widens as
      // they approach the camera (perspective foreshortening).
      // Use a continuously increasing scroll offset and modulo the index so
      // lines fade in at the horizon and fade out at the bottom.
      for (let i = 0; i < HORIZONTAL_LINES; i += 1) {
        const raw = (i + scroll) % HORIZONTAL_LINES;
        const t = raw / HORIZONTAL_LINES; // 0 = horizon, 1 = bottom
        const y = horizonY + Math.pow(t, 2.4) * groundHeight;
        // Fade in & out at the extremes so the cycling is invisible.
        const fadeIn = Math.min(1, t * 6);
        const fadeOut = Math.min(1, (1 - t) * 4);
        const alpha = 0.65 * fadeIn * fadeOut;
        ctx.strokeStyle = rgba(gridColor, alpha);
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
    };

    const tick = () => {
      const { skyTop, skyBottom, grid, sun } = colorsRef.current;
      drawSky(skyTop, skyBottom);
      drawSun(sun, skyBottom);
      drawGrid(grid);
      scroll += SCROLL_SPEED * HORIZONTAL_LINES;
      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
